import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaPrint,
  FaFileExcel,
  FaCheck,
  FaBan,
  FaFilter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../../../context/TitleContext";
import {
  fetchEmployeeLoans,
  deleteEmployeeLoan,
  approveEmployeeLoan,
  cancelEmployeeLoan,
} from "../../../../redux/Slices/EmployeeLoansSlice";
import { getEmployees } from "../../../../redux/Slices/EmployeesSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toPositiveInteger } from "../../../../shared/utils.js/utils";
import { isOnline } from "../../../../shared/utils.js/offlineQueue";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import DropdownWithSearch from "../../../../shared/components/DropdownWithSearch";

const EmployeeLoans = () => {
  const { t, i18n } = useTranslation("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";

  const { employeeLoans } = useSelector((state) => state.employeeLoans);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  // Preview State
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Print State
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allLoansForPdf, setAllLoansForPdf] = useState([]);

  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  // --- Search / Filter / Sort State ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filters, setFilters] = useState({
    employee_id: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  // Employee dropdown
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeePagination, setEmployeePagination] = useState(null);
  const employeeSearchInputRef = useRef(null);

  // Raw local state for all loans
  const [rawEmployeeLoans, setRawEmployeeLoans] = useState([]);
  const [isLoadingLoans, setIsLoadingLoans] = useState(false);

  useEffect(() => {
    setTitle("sidenav.hr.employeeLoansNamespace.title");
  }, [setTitle]);

  // Fetch all on mount (per_page: 1000 for client-side ops)
  const loadAllLoans = useCallback(async () => {
    setIsLoadingLoans(true);
    try {
      await dispatch(fetchEmployeeLoans({ page: 1, per_page: 1000 })).unwrap();
    } catch (err) {
      if (err !== "OFFLINE_REQUEST_SAVED") {
        console.error("Failed to fetch employee loans:", err);
      }
    } finally {
      setIsLoadingLoans(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadAllLoans();
  }, [loadAllLoans]);

  // Sync Redux → local raw state
  useEffect(() => {
    if (employeeLoans) {
      const list = Array.isArray(employeeLoans)
        ? employeeLoans
        : employeeLoans.data || [];
      setRawEmployeeLoans(list);
    }
  }, [employeeLoans]);

  // Offline sync listener
  useEffect(() => {
    const handleSync = async (event) => {
      const detail = event?.detail || {};
      if (detail.count > 0 && detail.results) {
        const hasLoanRequest = detail.results.some(
          (r) =>
            r.success === true &&
            r.request?.url?.includes("/employee-loans") &&
            ["POST", "PUT", "PATCH", "DELETE"].includes(r.request?.method)
        );
        if (hasLoanRequest && isOnline()) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          loadAllLoans();
        }
      }
    };
    window.addEventListener("offlineRequestsRetried", handleSync);
    return () => window.removeEventListener("offlineRequestsRetried", handleSync);
  }, [loadAllLoans]);

  // Helper: normalize list response from API
  const normalizeListResponse = (payload) => {
    if (!payload) return { items: [], pagination: null };
    let items = [];
    let pagination = null;
    if (Array.isArray(payload?.data?.data)) {
      items = payload.data.data;
      pagination = payload.data?.meta || payload.data?.pagination || null;
    } else if (Array.isArray(payload?.data)) {
      items = payload.data;
      pagination = payload.meta || payload.pagination || null;
    } else if (Array.isArray(payload)) {
      items = payload;
    } else {
      items = [];
    }
    if (pagination) {
      pagination = {
        current_page: pagination.current_page || 1,
        last_page: pagination.last_page || pagination.total_pages || 1,
        per_page: pagination.per_page || 10,
        total: pagination.total || 0,
      };
    }
    return { items, pagination };
  };

  // Fetch employees for filter dropdown
  const fetchEmployeesForFilter = useCallback(
    async (page = employeePage, search = employeeSearchTerm) => {
      try {
        const payload = await dispatch(
          getEmployees({ page, per_page: 10, search: search || undefined, language: i18n.language })
        ).unwrap();
        const { items, pagination: paginationMeta } = normalizeListResponse(payload);
        setEmployeeOptions(items);
        setEmployeePagination(paginationMeta);
      } catch (error) {
        console.error("Failed to fetch employees for filter:", error);
      }
    },
    [dispatch, i18n.language]
  );

  useEffect(() => {
    if (employeeDropdownOpen) {
      fetchEmployeesForFilter(employeePage, employeeSearchTerm);
    }
  }, [employeeDropdownOpen, employeePage, fetchEmployeesForFilter]);

  useEffect(() => {
    if (employeeSearchTerm !== undefined && employeeDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEmployeePage(1);
        fetchEmployeesForFilter(1, employeeSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [employeeSearchTerm, employeeDropdownOpen, fetchEmployeesForFilter]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown-container]") &&
        !event.target.closest("[data-dropdown-toggle]")
      ) {
        setEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Client-side Filter + Search + Sort ---
  const filteredEmployeeLoans = React.useMemo(() => {
    let data = [...rawEmployeeLoans];

    // 1. Search term
    const query = searchTerm.trim().replace(/\s+/g, " ").toLowerCase();
    if (query) {
      data = data.filter((item) => {
        const empNameAr = (item.employee?.name_ar || "").toLowerCase();
        const empNameEn = (item.employee?.name_en || "").toLowerCase();
        const empName = (item.employee?.name || "").toLowerCase();
        const empCode = (item.employee?.employee_code || "").toLowerCase();
        const amount = item.amount ? String(item.amount) : "";
        const paidAmount = item.paid_amount ? String(item.paid_amount) : "";
        const remaining = item.remaining_balance ? String(item.remaining_balance) : "";
        const monthly = item.monthly_deduction ? String(item.monthly_deduction) : "";
        const progress = item.payment_progress ? String(item.payment_progress) : "";
        const loanDate = (item.loan_date || "").toLowerCase();
        const status = (item.status || "").toLowerCase();
        const statusTrans = t(`sidenav.hr.employeeLoansNamespace.${item.status}`).toLowerCase();
        const desc = (item.description || "").toLowerCase();

        return (
          empNameAr.includes(query) ||
          empNameEn.includes(query) ||
          empName.includes(query) ||
          empCode.includes(query) ||
          amount.includes(query) ||
          paidAmount.includes(query) ||
          remaining.includes(query) ||
          monthly.includes(query) ||
          progress.includes(query) ||
          loanDate.includes(query) ||
          status.includes(query) ||
          statusTrans.includes(query) ||
          desc.includes(query)
        );
      });
    }

    // 2. Employee filter
    if (filters.employee_id) {
      data = data.filter(
        (item) => String(item.employee?.id) === String(filters.employee_id)
      );
    }

    // 3. Status filter
    if (filters.status) {
      data = data.filter((item) => item.status === filters.status);
    }

    // 4. Date range filter
    if (filters.start_date) {
      const start = new Date(filters.start_date).getTime();
      data = data.filter((item) => new Date(item.loan_date).getTime() >= start);
    }
    if (filters.end_date) {
      const end = new Date(filters.end_date).getTime();
      data = data.filter((item) => new Date(item.loan_date).getTime() <= end);
    }

    // 5. Sorting
    if (sortField && sortOrder) {
      data.sort((a, b) => {
        let aVal = "";
        let bVal = "";

        if (sortField === "employee") {
          aVal = isRTL
            ? a.employee?.name_ar || a.employee?.name || ""
            : a.employee?.name_en || a.employee?.name || "";
          bVal = isRTL
            ? b.employee?.name_ar || b.employee?.name || ""
            : b.employee?.name_en || b.employee?.name || "";
        } else if (sortField === "amount") {
          return sortOrder === "asc"
            ? (Number(a.amount) || 0) - (Number(b.amount) || 0)
            : (Number(b.amount) || 0) - (Number(a.amount) || 0);
        } else if (sortField === "paid_amount") {
          return sortOrder === "asc"
            ? (Number(a.paid_amount) || 0) - (Number(b.paid_amount) || 0)
            : (Number(b.paid_amount) || 0) - (Number(a.paid_amount) || 0);
        } else if (sortField === "remaining_balance") {
          return sortOrder === "asc"
            ? (Number(a.remaining_balance) || 0) - (Number(b.remaining_balance) || 0)
            : (Number(b.remaining_balance) || 0) - (Number(a.remaining_balance) || 0);
        } else if (sortField === "loan_date") {
          const aTime = new Date(a.loan_date || 0).getTime();
          const bTime = new Date(b.loan_date || 0).getTime();
          return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
        } else if (sortField === "status") {
          aVal = t(`sidenav.hr.employeeLoansNamespace.${a.status}`);
          bVal = t(`sidenav.hr.employeeLoansNamespace.${b.status}`);
        } else if (sortField === "payment_progress") {
          return sortOrder === "asc"
            ? (Number(a.payment_progress) || 0) - (Number(b.payment_progress) || 0)
            : (Number(b.payment_progress) || 0) - (Number(a.payment_progress) || 0);
        }

        return sortOrder === "asc"
          ? aVal.localeCompare(bVal, isRTL ? "ar" : "en", { sensitivity: "base" })
          : bVal.localeCompare(aVal, isRTL ? "ar" : "en", { sensitivity: "base" });
      });
    }

    return data;
  }, [rawEmployeeLoans, searchTerm, filters, sortField, sortOrder, isRTL, t]);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredEmployeeLoans.length / perPage));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedLoans = filteredEmployeeLoans.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage
  );

  const handleApplyPerPage = () => {
    const safePerPage = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safePerPage);
    if (safePerPage !== perPage) {
      setPerPage(safePerPage);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ employee_id: "", status: "", start_date: "", end_date: "" });
    setSearchTerm("");
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  };

  // Sort toggle: null → asc → desc → null
  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ color: "white", marginInlineStart: "6px", fontSize: "12px" }}>⇅</span>;
    if (sortOrder === "asc") return <span style={{ color: "white", marginInlineStart: "6px", fontSize: "12px" }}>▲</span>;
    return <span style={{ color: "white", marginInlineStart: "6px", fontSize: "12px" }}>▼</span>;
  };

  const handleEdit = (loan) => navigate(`/employee-loans/edit/${loan.id}`);

  const handleDeleteClick = (loan) => {
    setSelectedLoan(loan);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLoan) return;
    const backup = selectedLoan;
    setRawEmployeeLoans((prev) => prev.filter((l) => l.id !== backup.id));
    setShowDeleteModal(false);
    setSelectedLoan(null);
    try {
      const result = await dispatch(deleteEmployeeLoan(backup.id)).unwrap();
      if (result && result.isOffline) {
        localStorage.setItem("offline_add_performed", "true");
        toast.success(
          isRTL
            ? "تم حذف السلفة محلياً وسيتم المزامنة عند عودة الاتصال"
            : "Loan deleted locally and will sync when online"
        );
      } else {
        toast.success(t("sidenav.hr.employeeLoansNamespace.deleteSuccess"));
      }
      loadAllLoans();
    } catch (error) {
      setRawEmployeeLoans((prev) => [...prev, backup]);
      toast.error(error?.message || t("sidenav.hr.employeeLoansNamespace.errorAction"));
    }
  };

  const handleApprove = async (loan) => {
    try {
      const result = await dispatch(approveEmployeeLoan(loan.id)).unwrap();
      if (result && result.isOffline) {
        localStorage.setItem("offline_add_performed", "true");
        toast.success(isRTL ? "تمت الموافقة محلياً وسيتم المزامنة" : "Approved locally and will sync");
      } else {
        toast.success(t("sidenav.hr.employeeLoansNamespace.approveSuccess"));
      }
      loadAllLoans();
    } catch (error) {
      toast.error(error?.message || t("sidenav.hr.employeeLoansNamespace.errorAction"));
    }
  };

  const handleCancel = async (loan) => {
    try {
      const result = await dispatch(cancelEmployeeLoan(loan.id)).unwrap();
      if (result && result.isOffline) {
        localStorage.setItem("offline_add_performed", "true");
        toast.success(isRTL ? "تم الإلغاء محلياً وسيتم المزامنة" : "Cancelled locally and will sync");
      } else {
        toast.success(t("sidenav.hr.employeeLoansNamespace.cancelSuccess"));
      }
      loadAllLoans();
    } catch (error) {
      toast.error(error?.message || t("sidenav.hr.employeeLoansNamespace.errorAction"));
    }
  };

  // --- Dynamic PDF Chunking ---
  const colStyles = {
    serial:   { width: "5%",  textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    employee: { width: "22%", textAlign: isRTL ? "right" : "left", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px", wordBreak: "break-word" },
    amount:   { width: "10%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    paid:     { width: "10%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    remaining:{ width: "10%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    monthly:  { width: "10%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    progress: { width: "8%",  textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    date:     { width: "11%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
    status:   { width: "14%", textAlign: "center", verticalAlign: "middle", border: "1px solid #000", padding: "5px 2px", fontSize: "9px" },
  };

  const calculateDynamicChunks = useCallback((list) => {
    const container = document.getElementById("employee-loans-measurement-container");
    if (!container) return [list];
    const tbody = container.querySelector("tbody");
    if (!tbody) return [list];

    const maxHeight = 700; // px — usable area inside A4 page after header/footer
    const chunks = [];
    let currentChunk = [];
    let currentHeight = 0;

    const rows = tbody.querySelectorAll("tr");
    for (let i = 0; i < rows.length && i < list.length; i++) {
      const rowHeight = rows[i].offsetHeight || 22;
      if (currentHeight + rowHeight > maxHeight && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [list[i]];
        currentHeight = rowHeight;
      } else {
        currentChunk.push(list[i]);
        currentHeight += rowHeight;
      }
    }
    if (currentChunk.length > 0) chunks.push(currentChunk);
    return chunks.length > 0 ? chunks : [list];
  }, []);

  // --- Preview Logic ---
  const handlePreviewSingle = (loan) => {
    setPreviewData({ type: "single", data: loan });
    setShowPreviewModal(true);
  };

  const handlePreviewAll = () => {
    const list = filteredEmployeeLoans;
    if (list.length === 0) {
      toast.error(t("sidenav.hr.employeeLoansNamespace.noData"));
      return;
    }
    setAllLoansForPdf(list);
    // Allow measurement table to render
    requestAnimationFrame(() => {
      setTimeout(() => {
        const chunks = calculateDynamicChunks(list);
        setPreviewData({ type: "all", data: list, chunks });
        setShowPreviewModal(true);
      }, 200);
    });
  };

  const handlePrintFromPreview = async () => {
    if (!previewData) return;
    setShowPreviewModal(false);
    if (previewData.type === "single") {
      await handlePrintSingle(previewData.data);
    } else {
      await handlePrintAll();
    }
  };

  const handlePrintSingle = async (loan) => {
    setSelectedLoan(loan);
    setPdfType("single");
    await new Promise((resolve) => setTimeout(resolve, 300));
    setIsGeneratingPdf(true);
    try {
      const element = document.getElementById(`employee-loan-pdf-single-${loan.id}`);
      if (!element) throw new Error("Template not found");
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png", 1.0), "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      pdf.save(`Loan_${loan.id}.pdf`);
      toast.success(t("btns.download"));
    } catch (err) {
      console.error(err);
      toast.error(t("sidenav.hr.employeeLoansNamespace.errorAction"));
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setSelectedLoan(null);
    }
  };

  const handlePrintAll = async () => {
    const list = filteredEmployeeLoans;
    if (!list || list.length === 0) {
      toast.error(t("sidenav.hr.employeeLoansNamespace.noData"));
      return;
    }
    setAllLoansForPdf(list);

    // Wait for measurement table to render then calculate chunks
    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(list);
    setPdfChunks(chunks);
    setPdfType("all");
    await new Promise((resolve) => setTimeout(resolve, 600));
    setIsGeneratingPdf(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      for (let i = 0; i < chunks.length; i++) {
        const element = document.getElementById(`employee-loans-pdf-all-${i}`);
        if (!element) continue;
        const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      }
      pdf.save(`${t("sidenav.hr.employeeLoansNamespace.print.fileName")}.pdf`);
      toast.success(t("btns.download"));
    } catch (err) {
      console.error(err);
      toast.error(t("sidenav.hr.employeeLoansNamespace.errorAction"));
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
      setAllLoansForPdf([]);
    }
  };

  // --- Excel Export (from current filtered state) ---
  const handleExportExcel = () => {
    try {
      const list = filteredEmployeeLoans;
      if (list.length === 0) {
        toast.error(t("sidenav.hr.employeeLoansNamespace.noData"));
        return;
      }
      const excelData = list.map((item, index) => ({
        "#": index + 1,
        [t("sidenav.hr.employeeLoansNamespace.employee")]:
          isRTL
            ? item.employee?.name_ar || item.employee?.name || ""
            : item.employee?.name_en || item.employee?.name || "",
        [t("sidenav.hr.employeeLoansNamespace.amount")]: item.amount,
        [t("sidenav.hr.employeeLoansNamespace.paidAmount")]: item.paid_amount,
        [t("sidenav.hr.employeeLoansNamespace.remainingBalance")]: item.remaining_balance,
        [t("sidenav.hr.employeeLoansNamespace.monthlyDeduction")]: item.monthly_deduction,
        [t("sidenav.hr.employeeLoansNamespace.paymentProgress")]: `${item.payment_progress}%`,
        [t("sidenav.hr.employeeLoansNamespace.installmentsCount")]: item.installments_count,
        [t("sidenav.hr.employeeLoansNamespace.loanDate")]: item.loan_date,
        [t("sidenav.hr.employeeLoansNamespace.status")]: t(`sidenav.hr.employeeLoansNamespace.${item.status}`),
        [t("sidenav.hr.employeeLoansNamespace.description")]: item.description || "",
      }));
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isRTL ? "السلف" : "Employee Loans");
      const fileName = `${isRTL ? "سلف_الموظفين" : "Employee_Loans"}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(isRTL ? "تم تصدير ملف Excel بنجاح" : "Excel exported successfully");
    } catch (e) {
      console.error(e);
      toast.error(t("sidenav.hr.employeeLoansNamespace.errorAction"));
    }
  };

  // --- PDF Render Functions (inner — close over component state) ---
  const renderSinglePdfPage = (loan, isPreviewMode = false) => {
    const containerId = isPreviewMode
      ? `preview-employee-loan-pdf-${loan.id}`
      : `employee-loan-pdf-single-${loan.id}`;

    const rows = [
      { label: t("sidenav.hr.employeeLoansNamespace.loanDate"), value: loan.loan_date || "—", accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.employee"), value: isRTL ? (loan.employee?.name_ar || loan.employee?.name || "") : (loan.employee?.name_en || loan.employee?.name || "") },
      { label: t("sidenav.hr.employeeLoansNamespace.amount"), value: parseFloat(loan.amount || 0).toLocaleString(), accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.paidAmount"), value: parseFloat(loan.paid_amount || 0).toLocaleString(), accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.remainingBalance"), value: parseFloat(loan.remaining_balance || 0).toLocaleString(), accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.monthlyDeduction"), value: parseFloat(loan.monthly_deduction || 0).toLocaleString(), accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.paymentProgress"), value: `${loan.payment_progress || 0}%`, accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.installmentsCount"), value: loan.installments_count, accent: true },
      { label: t("sidenav.hr.employeeLoansNamespace.status"), value: t(`sidenav.hr.employeeLoansNamespace.${loan.status}`), accent: true, statusVal: loan.status },
      { label: t("sidenav.hr.employeeLoansNamespace.description"), value: loan.description || "—" },
    ];

    return (
      <div
        id={containerId}
        style={{ width: "794px", height: "1123px", backgroundColor: "#ffffff", color: "#000000", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: "bold", padding: "40px", boxSizing: "border-box", boxShadow: isPreviewMode ? "0 4px 20px rgba(0,0,0,0.5)" : "none", overflow: "hidden" }}
      >
        <div style={{ border: "5px solid #000", padding: "3px", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%", height: "1043px", overflow: "hidden" }}>
          <div style={{ border: "2px solid #000", height: "1033px", padding: "18px 18px 10px", position: "relative", display: "flex", flexDirection: "column", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: "bold", direction: isRTL ? "rtl" : "ltr", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundImage: `url(${headerLogo})`, backgroundSize: "95%", backgroundPosition: "center center", backgroundRepeat: "no-repeat", opacity: 0.08, zIndex: 0, pointerEvents: "none" }} />
            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: "100%", height: "auto", objectFit: "contain", marginBottom: "8px", borderBottom: "4px double #000", paddingBottom: "8px", display: "block", flexShrink: 0 }} crossOrigin="anonymous" />
              <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", flexShrink: 0 }}>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تفاصيل سلفة الموظف" : "Employee Loan Details"}</div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>ID: #{loan.id}</div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", paddingBottom: "120px" }}>
                <div style={{ marginTop: "12px", border: "2px solid #000", borderRadius: "4px", overflow: "hidden", backgroundColor: "rgba(255,255,255,0.3)" }}>
                  {rows.map((row, idx) => {
                    let valColor = "#000";
                    if (row.statusVal) {
                      valColor = row.statusVal === "approved" || row.statusVal === "fully_paid" ? "#28a745" : row.statusVal === "cancelled" ? "#dc3545" : "#ffc107";
                    } else if (row.accent) {
                      valColor = "#0057c2";
                    }
                    return (
                      <div key={idx} style={{ display: "flex", borderBottom: idx === rows.length - 1 ? "none" : "1px solid #000", minHeight: "38px", backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.03)" : "transparent" }}>
                        {isRTL ? (
                          <>
                            <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 15px", fontSize: "14px", fontWeight: row.accent ? "700" : "500", color: valColor, direction: "rtl" }}>{row.value}</div>
                            <div style={{ width: "30%", borderRight: "1px solid #000", backgroundColor: "rgba(227,242,253,0.4)", display: "flex", alignItems: "center", padding: "0 15px", fontWeight: "700", fontSize: "13px", color: "#1976d2", direction: "rtl" }}>{row.label}</div>
                          </>
                        ) : (
                          <>
                            <div style={{ width: "30%", borderRight: "1px solid #000", backgroundColor: "rgba(227,242,253,0.4)", display: "flex", alignItems: "center", padding: "0 15px", fontWeight: "700", fontSize: "13px", color: "#1976d2" }}>{row.label}</div>
                            <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 15px", fontSize: "14px", fontWeight: row.accent ? "700" : "500", color: valColor }}>{row.value}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ position: "absolute", bottom: "10px", left: "18px", right: "18px" }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: "100%", height: "auto", objectFit: "contain", display: "block", borderTop: "4px double #000", paddingTop: "8px" }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPdfPage = (chunk, chunkIdx, totalChunks, elementId = null) => {
    const hasActiveFilters = searchTerm.trim() || filters.employee_id || filters.status || filters.start_date || filters.end_date;

    return (
      <div
        id={elementId}
        style={{ width: "794px", height: "1123px", backgroundColor: "#ffffff", color: "#000000", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: "bold", padding: "40px", boxSizing: "border-box", overflow: "hidden" }}
      >
        <div style={{ border: "5px solid #000", padding: "3px", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%", height: "1043px", overflow: "hidden" }}>
          <div style={{ border: "2px solid #000", height: "1033px", padding: "18px 18px 10px", position: "relative", display: "flex", flexDirection: "column", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: "bold", direction: isRTL ? "rtl" : "ltr", backgroundColor: "#ffffff", boxSizing: "border-box", width: "100%", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundImage: `url(${headerLogo})`, backgroundSize: "60%", backgroundPosition: "center top", backgroundRepeat: "no-repeat", opacity: 0.05, zIndex: 0, pointerEvents: "none" }} />
            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: "100%", height: "auto", objectFit: "contain", marginBottom: "8px", borderBottom: "4px double #000", paddingBottom: "8px", display: "block", flexShrink: 0 }} crossOrigin="anonymous" />
              <div style={{ textAlign: "center", padding: "6px 0 8px", borderBottom: "1px solid #ddd", marginBottom: "4px", flexShrink: 0 }}>
                <div style={{ fontSize: "16px", fontWeight: "900", color: "#000080" }}>{isRTL ? "تقرير سلف الموظفين" : "Employee Loans Report"}</div>
                <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>
                  {isRTL ? "تاريخ التقرير" : "Report Date"}: {new Date().toLocaleDateString("en-US")} — {isRTL ? "صفحة" : "Page"} {chunkIdx + 1} / {totalChunks}
                </div>
                {hasActiveFilters && (
                  <div style={{ fontSize: "9px", color: "#444", marginTop: "6px", display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", backgroundColor: "rgba(238,242,247,0.5)", padding: "4px 8px", borderRadius: "4px", border: "1px solid #ddd" }}>
                    {searchTerm.trim() && <span>{isRTL ? "بحث" : "Search"}: {searchTerm.trim()}</span>}
                    {filters.employee_id && <span>{t("sidenav.hr.employeeLoansNamespace.employee")}: {employeeOptions.find((e) => String(e.id) === String(filters.employee_id))?.name || filters.employee_id}</span>}
                    {filters.status && <span>{t("sidenav.hr.employeeLoansNamespace.status")}: {t(`sidenav.hr.employeeLoansNamespace.${filters.status}`)}</span>}
                    {(filters.start_date || filters.end_date) && <span>{t("sidenav.hr.employeeLoansNamespace.loanDate")}: {filters.start_date || "..."} {isRTL ? "إلى" : "to"} {filters.end_date || "..."}</span>}
                  </div>
                )}
              </div>
              <div style={{ marginTop: "10px", direction: isRTL ? "rtl" : "ltr", flex: 1, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", fontFamily: '"Times New Roman", serif', backgroundColor: "rgba(255,255,255,0.7)", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: "rgba(238,242,247,0.7)" }}>
                      <th style={colStyles.serial}>#</th>
                      <th style={colStyles.employee}>{t("sidenav.hr.employeeLoansNamespace.employee")}</th>
                      <th style={colStyles.amount}>{t("sidenav.hr.employeeLoansNamespace.amount")}</th>
                      <th style={colStyles.paid}>{t("sidenav.hr.employeeLoansNamespace.paidAmount")}</th>
                      <th style={colStyles.remaining}>{t("sidenav.hr.employeeLoansNamespace.remainingBalance")}</th>
                      <th style={colStyles.monthly}>{t("sidenav.hr.employeeLoansNamespace.monthlyDeduction")}</th>
                      <th style={colStyles.progress}>{t("sidenav.hr.employeeLoansNamespace.paymentProgress")}</th>
                      <th style={colStyles.date}>{t("sidenav.hr.employeeLoansNamespace.loanDate")}</th>
                      <th style={colStyles.status}>{t("sidenav.hr.employeeLoansNamespace.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk && chunk.length > 0 ? chunk.map((item, idx) => {
                      const foundIdx = allLoansForPdf.findIndex((l) => String(l?.id) === String(item?.id));
                      const serial = foundIdx >= 0 ? foundIdx + 1 : chunkIdx * 25 + idx + 1;
                      const cellBg = idx % 2 === 0 ? "rgba(255,255,255,0.7)" : "rgba(245,245,245,0.5)";
                      return (
                        <tr key={item.id} style={{ backgroundColor: cellBg }}>
                          <td style={{ ...colStyles.serial, backgroundColor: cellBg }}>{serial}</td>
                          <td style={{ ...colStyles.employee, backgroundColor: cellBg }}>{isRTL ? (item.employee?.name_ar || item.employee?.name || "") : (item.employee?.name_en || item.employee?.name || "")}</td>
                          <td style={{ ...colStyles.amount, backgroundColor: cellBg }}>{parseFloat(item.amount || 0).toLocaleString()}</td>
                          <td style={{ ...colStyles.paid, backgroundColor: cellBg }}>{parseFloat(item.paid_amount || 0).toLocaleString()}</td>
                          <td style={{ ...colStyles.remaining, backgroundColor: cellBg }}>{parseFloat(item.remaining_balance || 0).toLocaleString()}</td>
                          <td style={{ ...colStyles.monthly, backgroundColor: cellBg }}>{parseFloat(item.monthly_deduction || 0).toLocaleString()}</td>
                          <td style={{ ...colStyles.progress, backgroundColor: cellBg }}>{item.payment_progress || 0}%</td>
                          <td style={{ ...colStyles.date, backgroundColor: cellBg }}>{item.loan_date}</td>
                          <td style={{ ...colStyles.status, backgroundColor: cellBg, color: item.status === "approved" || item.status === "fully_paid" ? "#28a745" : item.status === "cancelled" ? "#dc3545" : "#ffc107" }}>
                            {t(`sidenav.hr.employeeLoansNamespace.${item.status}`)}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan="9" style={{ border: "1px solid #000", padding: "15px", textAlign: "center", color: "#666", fontSize: "12px" }}>{t("sidenav.hr.employeeLoansNamespace.noData")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ position: "absolute", bottom: "10px", left: "18px", right: "18px" }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: "100%", height: "auto", objectFit: "contain", display: "block", borderTop: "4px double #000", paddingTop: "8px" }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div style={{ padding: "30px", backgroundColor: "#14263b", minHeight: "calc(100vh - 80px)", color: "white", direction: dir }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", flexWrap: "wrap", gap: "20px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0, fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
          {t("sidenav.hr.employeeLoansNamespace.title")}
        </h1>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={() => navigate("/employee-loans/add")} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", backgroundColor: "#AC2000", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
            <FaPlus /> {t("sidenav.hr.employeeLoansNamespace.add")}
          </button>
          <button onClick={handlePreviewAll} disabled={isGeneratingPdf} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", backgroundColor: "#17a2b8", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
            <FaEye /> {t("sidenav.hr.penaltyTypesNamespace.previewAll")}
          </button>
          <button onClick={handlePrintAll} disabled={isGeneratingPdf} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
            <FaPrint /> {t("sidenav.hr.penaltyTypesNamespace.printAll")}
          </button>
          <button onClick={handleExportExcel} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
            <FaFileExcel /> {t("sidenav.hr.penaltyTypesNamespace.exportAll")}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ backgroundColor: "#1a1f2e", padding: "20px", borderRadius: "12px", border: "1px solid #333", marginBottom: "0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          <div style={{ position: "relative", flex: "1", minWidth: "280px", maxWidth: "500px" }}>
            <FaSearch style={{ position: "absolute", top: "50%", left: isRTL ? "auto" : "15px", right: isRTL ? "15px" : "auto", transform: "translateY(-50%)", color: "#666" }} />
            <input
              type="text"
              placeholder={t("sidenav.hr.employeeLoansNamespace.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{ width: "100%", padding: "12px 45px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", outline: "none", fontSize: "15px", boxSizing: "border-box" }}
            />
          </div>
          <button
            onClick={() => setIsFilterOpen((o) => !o)}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", backgroundColor: isFilterOpen ? "#AC2000" : "#2d3748", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}
          >
            <FaFilter /> {isRTL ? "الفلاتر" : "Filters"}
          </button>
        </div>

        {/* Filter Panel */}
        {isFilterOpen && (
          <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #333" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "20px", marginBottom: "20px" }}>

              {/* Employee Filter */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                  {t("sidenav.hr.employeeLoansNamespace.employee")}
                </label>
                <div style={{ position: "relative" }} data-dropdown-container="true">
                  <div
                    data-dropdown-toggle="true"
                    onClick={() => setEmployeeDropdownOpen((o) => !o)}
                    style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: filters.employee_id ? "white" : "#666", fontSize: "14px", minHeight: "45px", direction: isRTL ? "rtl" : "ltr", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <span>
                      {filters.employee_id
                        ? (() => {
                            const emp = employeeOptions.find((e) => String(e.id) === String(filters.employee_id));
                            if (!emp) return isRTL ? "موظف محدد" : "Selected";
                            return isRTL
                              ? (emp.name_ar || emp.name_en || emp.name || (isRTL ? "موظف محدد" : "Selected"))
                              : (emp.name_en || emp.name_ar || emp.name || "Selected");
                          })()
                        : (isRTL ? "جميع الموظفين" : "All Employees")}
                    </span>
                    {filters.employee_id ? (
                      <span onClick={(e) => { e.stopPropagation(); handleFilterChange("employee_id", ""); setEmployeeDropdownOpen(false); }} style={{ cursor: "pointer", color: "#dc3545", fontSize: "12px", padding: "2px 4px" }}>✕</span>
                    ) : (
                      <span style={{ fontSize: 12 }}>▼</span>
                    )}
                  </div>
                  {employeeDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={employeeDropdownOpen}
                      searchTerm={employeeSearchTerm}
                      onSearchChange={setEmployeeSearchTerm}
                      searchInputRef={employeeSearchInputRef}
                      items={employeeOptions}
                      pagination={employeePagination}
                      currentPage={employeePage}
                      onPageChange={setEmployeePage}
                      onClose={() => setEmployeeDropdownOpen(false)}
                      onSelect={(id) => {
                        handleFilterChange("employee_id", String(id));
                        setEmployeeDropdownOpen(false);
                        setEmployeeSearchTerm("");
                        setEmployeePage(1);
                      }}
                      label={isRTL ? "الموظف" : "Employee"}
                      isRTL={isRTL}
                      t={t}
                      searchPlaceholder={isRTL ? "ابحث عن موظف..." : "Search for employee..."}
                    />
                  )}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                  {t("sidenav.hr.employeeLoansNamespace.status")}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", minHeight: "45px", direction: isRTL ? "rtl" : "ltr", outline: "none" }}
                >
                  <option value="">{isRTL ? "الكل" : "All"}</option>
                  <option value="pending">{t("sidenav.hr.employeeLoansNamespace.pending")}</option>
                  <option value="approved">{t("sidenav.hr.employeeLoansNamespace.approved")}</option>
                  <option value="fully_paid">{t("sidenav.hr.employeeLoansNamespace.fully_paid")}</option>
                  <option value="cancelled">{t("sidenav.hr.employeeLoansNamespace.cancelled")}</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                  {isRTL ? "من تاريخ" : "Start Date"}
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                  style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", minHeight: "45px", direction: isRTL ? "rtl" : "ltr", outline: "none", boxSizing: "border-box" }}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", color: "#ccc", fontSize: "14px", fontWeight: "500", textAlign: isRTL ? "right" : "left" }}>
                  {isRTL ? "إلى تاريخ" : "End Date"}
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                  style={{ width: "100%", padding: "12px 15px", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "8px", color: "white", fontSize: "14px", minHeight: "45px", direction: isRTL ? "rtl" : "ltr", outline: "none", boxSizing: "border-box" }}
                />
              </div>
            </div>

            {/* Filter Actions + Per Page */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px", borderTop: "1px solid #333", paddingTop: "15px" }}>
              <button onClick={clearFilters} style={{ padding: "10px 20px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                {isRTL ? "إعادة ضبط" : "Reset"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "14px", color: "#ccc", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                  {t("sidenav.hr.penaltyTypesNamespace.perPage")}
                </span>
                <div style={{ display: "flex", alignItems: "center", backgroundColor: "#14263b", border: "1px solid #333", borderRadius: "4px" }}>
                  <button onClick={() => setPerPageInput(Math.max(1, toPositiveInteger(perPageInput, perPage) - 1))} style={{ padding: "8px 12px", background: "none", border: "none", color: "white", cursor: "pointer", fontSize: "16px" }}>-</button>
                  <input type="number" value={perPageInput} onChange={(e) => setPerPageInput(toPositiveInteger(e.target.value, perPage))} style={{ width: "50px", background: "none", border: "none", color: "white", textAlign: "center", appearance: "textfield", margin: 0, fontWeight: "bold" }} />
                  <button onClick={() => setPerPageInput(toPositiveInteger(perPageInput, perPage) + 1)} style={{ padding: "8px 12px", background: "none", border: "none", color: "white", cursor: "pointer", fontSize: "16px" }}>+</button>
                </div>
                <button onClick={handleApplyPerPage} style={{ padding: "10px 20px", backgroundColor: "#AC2000", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold", fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                  {t("sidenav.hr.penaltyTypesNamespace.apply")}
                </button>
              </div>
            </div>

            {/* Results count */}
            <div style={{ marginTop: "10px", fontSize: "13px", color: "#aaa", textAlign: isRTL ? "right" : "left" }}>
              {isRTL
                ? `يعرض ${filteredEmployeeLoans.length} من أصل ${rawEmployeeLoans.length} سجل`
                : `Showing ${filteredEmployeeLoans.length} of ${rawEmployeeLoans.length} records`}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ backgroundColor: "#1b3350", borderRadius: "12px", border: "1px solid #333", overflow: "hidden", marginTop: "20px" }}>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table style={{ minWidth: "1100px", width: "100%", borderCollapse: "collapse", textAlign: textAlign }}>
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap" }}>#</th>
                <th onClick={() => handleSort("employee")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.employee")} {getSortIcon("employee")}
                </th>
                <th onClick={() => handleSort("amount")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.amount")} {getSortIcon("amount")}
                </th>
                <th onClick={() => handleSort("paid_amount")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.paidAmount")} {getSortIcon("paid_amount")}
                </th>
                <th onClick={() => handleSort("remaining_balance")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.remainingBalance")} {getSortIcon("remaining_balance")}
                </th>
                <th onClick={() => handleSort("monthly_deduction")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.monthlyDeduction")} {getSortIcon("monthly_deduction") }
                </th>
                <th onClick={() => handleSort("payment_progress")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.paymentProgress")} {getSortIcon("payment_progress")}
                </th>
                <th onClick={() => handleSort("loan_date")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.loanDate")} {getSortIcon("loan_date")}
                </th>
                <th onClick={() => handleSort("status")} style={{ padding: "15px", color: "white", fontWeight: "bold", whiteSpace: "nowrap", cursor: "pointer" }}>
                  {t("sidenav.hr.employeeLoansNamespace.status")} {getSortIcon("status")}
                </th>
                <th style={{ padding: "15px", color: "white", fontWeight: "bold", textAlign: "center", whiteSpace: "nowrap" }}>
                  {t("btns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoadingLoans && paginatedLoans.map((item, index) => (
                <tr key={item.id} style={{ borderBottom: "1px solid #2d3748", transition: "background 0.2s" }}>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>{(currentPageSafe - 1) * perPage + index + 1}</td>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>
                    {isRTL ? (item.employee?.name_ar || item.employee?.name || "") : (item.employee?.name_en || item.employee?.name || "")}
                  </td>
                  <td style={{ padding: "15px", fontWeight: "bold", whiteSpace: "nowrap" }}>{parseFloat(item.amount || 0).toLocaleString()}</td>
                  <td style={{ padding: "15px", color: "#28a745", whiteSpace: "nowrap" }}>{parseFloat(item.paid_amount || 0).toLocaleString()}</td>
                  <td style={{ padding: "15px", color: "#dc3545", whiteSpace: "nowrap" }}>{parseFloat(item.remaining_balance || 0).toLocaleString()}</td>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>{parseFloat(item.monthly_deduction || 0).toLocaleString()}</td>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>
                    <div style={{ width: "100%", minWidth: "100px", backgroundColor: "#14263b", borderRadius: "10px", height: "8px", position: "relative", overflow: "hidden" }}>
                      <div style={{ width: `${item.payment_progress || 0}%`, backgroundColor: "#AC2000", height: "100%", transition: "width 0.3s ease" }} />
                    </div>
                    <span style={{ fontSize: "10px", color: "#ccc" }}>{item.payment_progress || 0}%</span>
                  </td>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>{item.loan_date}</td>
                  <td style={{ padding: "15px", whiteSpace: "nowrap" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor: item.status === "approved" || item.status === "fully_paid" ? "#28a74522" : item.status === "cancelled" ? "#dc354522" : "#ffc10722",
                      color: item.status === "approved" || item.status === "fully_paid" ? "#28a745" : item.status === "cancelled" ? "#dc3545" : "#ffc107",
                    }}>
                      {t(`sidenav.hr.employeeLoansNamespace.${item.status}`)}
                    </span>
                  </td>
                  <td style={{ padding: "15px" }}>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                      {item.status === "pending" && (
                        <button title={t("sidenav.hr.employeeLoansNamespace.approve")} onClick={() => handleApprove(item)} style={{ backgroundColor: "#28a745", color: "white", border: "none", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                          <FaCheck />
                        </button>
                      )}
                      {item.status === "pending" && (
                        <button title={t("sidenav.hr.employeeLoansNamespace.cancel")} onClick={() => handleCancel(item)} style={{ backgroundColor: "#dc3545", color: "white", border: "none", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                          <FaBan />
                        </button>
                      )}
                      <button title={t("btns.view")} onClick={() => handlePreviewSingle(item)} style={{ backgroundColor: "#666", color: "white", border: "none", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                        <FaEye />
                      </button>
                      <button title={t("btns.edit")} onClick={() => handleEdit(item)} style={{ backgroundColor: "#AC2000", color: "white", border: "none", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                        <FaEdit />
                      </button>
                      <button title={t("btns.delete")} onClick={() => handleDeleteClick(item)} style={{ backgroundColor: "#dc3545", color: "white", border: "none", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoadingLoans && paginatedLoans.length === 0 && (
                <tr><td colSpan="10" style={{ padding: "30px", textAlign: "center", color: "#666", whiteSpace: "nowrap" }}>{t("sidenav.hr.employeeLoansNamespace.noData")}</td></tr>
              )}
              {isLoadingLoans && (
                <tr><td colSpan="10" style={{ padding: "30px", textAlign: "center", color: "#666", whiteSpace: "nowrap" }}>{t("messages.loading")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "20px" }}>
          <button onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))} disabled={currentPageSafe === 1} style={{ padding: "8px 12px", backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000", color: "white", border: "none", borderRadius: "4px", cursor: currentPageSafe === 1 ? "not-allowed" : "pointer" }}>
            {t("btns.previous")}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || (p >= currentPageSafe - 2 && p <= currentPageSafe + 2))
            .map((p, idx, arr) => (
              <React.Fragment key={p}>
                {idx > 0 && p - arr[idx - 1] > 1 && <span style={{ color: "#666" }}>...</span>}
                <button onClick={() => setCurrentPage(p)} style={{ padding: "8px 12px", backgroundColor: p === currentPageSafe ? "#AC2000" : "#1a1f2e", color: "white", border: "1px solid #333", borderRadius: "4px", cursor: "pointer" }}>
                  {p}
                </button>
              </React.Fragment>
            ))}
          <button onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))} disabled={currentPageSafe === totalPages} style={{ padding: "8px 12px", backgroundColor: currentPageSafe === totalPages ? "#333" : "#AC2000", color: "white", border: "none", borderRadius: "4px", cursor: currentPageSafe === totalPages ? "not-allowed" : "pointer" }}>
            {t("btns.next")}
          </button>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 }}>
          <div style={{ backgroundColor: "#1a1f2e", padding: "30px", borderRadius: "12px", maxWidth: "400px", width: "90%", textAlign: textAlign, direction: dir }}>
            <h2 style={{ fontSize: "20px", marginBottom: "15px", color: "#fff" }}>{t("sidenav.hr.employeeLoansNamespace.confirmDelete")}</h2>
            <p style={{ color: "#fff", marginBottom: "25px" }}>{t("sidenav.hr.employeeLoansNamespace.confirmDeleteMsg")}</p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ padding: "8px 20px", background: "none", border: "1px solid #333", color: "white", borderRadius: "6px", cursor: "pointer" }}>{t("btns.cancel")}</button>
              <button onClick={handleDeleteConfirm} style={{ padding: "8px 20px", backgroundColor: "#AC2000", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}>{t("btns.delete")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={showPreviewModal && previewData}
        title={isRTL ? "معاينة PDF" : "PDF Preview"}
        onClose={() => setShowPreviewModal(false)}
        onPrint={handlePrintFromPreview}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center" }}>
          {previewData?.type === "single" ? (
            renderSinglePdfPage(previewData.data, true)
          ) : (
            previewData?.chunks?.map((chunk, i) => (
              <React.Fragment key={i}>
                {renderPdfPage(chunk, i, previewData.chunks.length, null)}
              </React.Fragment>
            ))
          )}
        </div>
      </PdfPreviewModal>

      {/* Hidden Templates for printing ALL */}
      {pdfType === "all" && pdfChunks.map((chunk, i) => (
        <div key={i} id={`employee-loans-pdf-all-${i}`} style={{ position: "absolute", left: "-9999px", top: 0 }}>
          {renderPdfPage(chunk, i, pdfChunks.length, `employee-loans-pdf-all-${i}`)}
        </div>
      ))}

      {/* Hidden Template for printing SINGLE */}
      {pdfType === "single" && selectedLoan && (
        <div id={`employee-loan-pdf-single-${selectedLoan.id}`} style={{ position: "absolute", left: "-9999px", top: 0 }}>
          {renderSinglePdfPage(selectedLoan, false)}
        </div>
      )}

      {/* Hidden Measurement Table for dynamic PDF pagination */}
      <div
        id="employee-loans-measurement-container"
        style={{ position: "absolute", left: "-9999px", top: 0, width: "714px", visibility: "hidden", pointerEvents: "none" }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", fontFamily: '"Times New Roman", "Times", serif', tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: "rgba(238,242,247,0.7)" }}>
              <th style={colStyles.serial}>#</th>
              <th style={colStyles.employee}>{t("sidenav.hr.employeeLoansNamespace.employee")}</th>
              <th style={colStyles.amount}>{t("sidenav.hr.employeeLoansNamespace.amount")}</th>
              <th style={colStyles.paid}>{t("sidenav.hr.employeeLoansNamespace.paidAmount")}</th>
              <th style={colStyles.remaining}>{t("sidenav.hr.employeeLoansNamespace.remainingBalance")}</th>
              <th style={colStyles.monthly}>{t("sidenav.hr.employeeLoansNamespace.monthlyDeduction")}</th>
              <th style={colStyles.progress}>{t("sidenav.hr.employeeLoansNamespace.paymentProgress")}</th>
              <th style={colStyles.date}>{t("sidenav.hr.employeeLoansNamespace.loanDate")}</th>
              <th style={colStyles.status}>{t("sidenav.hr.employeeLoansNamespace.status")}</th>
            </tr>
          </thead>
          <tbody>
            {allLoansForPdf.map((item, idx) => (
              <tr key={item.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td style={{ ...colStyles.employee, direction: isRTL ? "rtl" : "ltr" }}>
                  {isRTL ? (item.employee?.name_ar || item.employee?.name || "") : (item.employee?.name_en || item.employee?.name || "")}
                </td>
                <td style={colStyles.amount}>{parseFloat(item.amount || 0).toLocaleString()}</td>
                <td style={colStyles.paid}>{parseFloat(item.paid_amount || 0).toLocaleString()}</td>
                <td style={colStyles.remaining}>{parseFloat(item.remaining_balance || 0).toLocaleString()}</td>
                <td style={colStyles.monthly}>{parseFloat(item.monthly_deduction || 0).toLocaleString()}</td>
                <td style={colStyles.progress}>{item.payment_progress || 0}%</td>
                <td style={colStyles.date}>{item.loan_date}</td>
                <td style={colStyles.status}>{t(`sidenav.hr.employeeLoansNamespace.${item.status}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeLoans;
